import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, Save, Share2, Copy, Trash2, Star, Users, Building2, X, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import TemplateEditor from './TemplateEditor';
import RecipeGallery from './RecipeGallery';
import {
  BASE_GROUP, FORMATS, MINE_GROUP, createMyTemplate, deleteMyTemplate, newTemplate, promoteMyTemplate, saveMyTemplate, saveTemplates,
  shareMyTemplate, templateEntries,
  type BrandKit, type BrandKitOut, type KitPerson, type ShareSettings, type Template, type TemplateEntry, type UserTemplate, type VisibleTemplates,
} from '@/lib/brand-kit';
import { api } from '@/lib/api';
import { qk } from '@/lib/queries';
import { cn } from '@/lib/cn';

const errText = (e: unknown, fallback = 'Could not save') => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: { msg?: string }) => x.msg).join('; ');
  return typeof d === 'string' ? d : fallback;
};

/** What is open in the editor: a saved entry, or a new design not saved yet. */
interface Draft {
  key: string;                          // entry key, or 'new'
  kind: TemplateEntry['kind'] | 'new';
  template: Template;
  row?: UserTemplate;
  /** new designs: where Save puts it */
  target?: 'mine' | 'base';
  based_on?: string | null;
  copy_of?: string | null;
}

/** "Share with the team on this clinic", "Share with specific people", "Share with the clinic". */
function ShareDialog({ row, people, onClose, onSaved, clientId }: {
  row: UserTemplate; people: { id: string; name: string }[]; clientId: string; onClose: () => void; onSaved: (r: UserTemplate) => void;
}) {
  const [s, setS] = useState<ShareSettings>({ shared_team: row.shared_team, shared_user_ids: row.shared_user_ids, shared_client: row.shared_client });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const others = people.filter((p) => p.id !== row.owner_user_id && p.name.toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    setBusy(true);
    try { const r = await shareMyTemplate(clientId, row.id, s); toast.success('Sharing saved'); onSaved(r); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Share this template" className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-xl border border-slate-800 bg-bg-card shadow-2xl p-5" data-testid="share-dialog">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base font-semibold text-muted-strong">Share "{row.name}"</h3>
            <p className="text-xs text-muted mt-1">Others see it read only and can use a copy. Nobody else sees it until you share it.</p>
          </div>
          <button onClick={onClose} className="p-1 text-muted hover:text-white" aria-label="Close"><X size={16} /></button>
        </div>
        <label className="flex items-start gap-2 py-2 text-sm text-muted-strong">
          <input type="checkbox" className="mt-1" checked={s.shared_team} onChange={(e) => setS({ ...s, shared_team: e.target.checked })} data-testid="share-team" />
          <span><Users size={14} className="inline mr-1" />Share with the team on this clinic<span className="block text-xs text-muted">Everyone who works on this clinic</span></span>
        </label>
        {people.length > 0 && <div className="py-2">
          <p className="text-sm text-muted-strong mb-1">Share with specific people</p>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people on this clinic" data-testid="share-search"
            className="w-full h-9 rounded-lg border border-slate-800 bg-bg px-3 text-sm text-muted-strong mb-2" />
          <div className="max-h-40 overflow-auto flex flex-col gap-0.5">
            {others.length === 0 && <p className="text-xs text-muted">Nobody else works on this clinic yet.</p>}
            {others.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-muted-strong px-1 h-8 rounded hover:bg-white/5">
                <input type="checkbox" checked={s.shared_user_ids.includes(p.id)} data-testid="share-person" data-person={p.id}
                  onChange={(e) => setS({ ...s, shared_user_ids: e.target.checked ? [...s.shared_user_ids, p.id] : s.shared_user_ids.filter((x) => x !== p.id) })} />
                {p.name}
              </label>
            ))}
          </div>
        </div>}
        <label className="flex items-start gap-2 py-2 text-sm text-muted-strong border-t border-slate-800">
          <input type="checkbox" className="mt-1" checked={s.shared_client} onChange={(e) => setS({ ...s, shared_client: e.target.checked })} data-testid="share-client" />
          <span><Building2 size={14} className="inline mr-1" />Share with the clinic<span className="block text-xs text-muted">The clinic's main portal login sees it under Designs and Brand kit. It cannot edit it.</span></span>
        </label>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={<Share2 size={14} />} loading={busy} onClick={save} data-testid="share-save">Save sharing</Button>
        </div>
      </div>
    </div>
  );
}

/** The Templates tab (owner 2026-10-24): everyone starts from the base templates; editing one saves
 *  your own copy (managers change the base itself); new designs are yours; share them with the team,
 *  with named people, or with the clinic. Owner and manager see everyone's, grouped by person. */
export default function TemplatesTab({ clientId, kit, people, visible, onBase }: {
  clientId: string; kit: BrandKit; people: KitPerson[]; visible: VisibleTemplates;
  /** the base templates changed on the server: the page keeps its kit in step */
  onBase: (out: BrandKitOut) => void;
}) {
  const qc = useQueryClient();
  const entries = useMemo(() => templateEntries(visible), [visible]);
  const canBase = visible.can_base;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipes, setRecipes] = useState(false);
  const [newTarget, setNewTarget] = useState<'mine' | 'base'>('mine');
  const [share, setShare] = useState<UserTemplate | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<{ title: string; message: string; run: () => Promise<void> } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.brandTemplates(clientId) });

  const open = (e: TemplateEntry) => setDraft({ key: e.key, kind: e.kind, template: structuredClone(e.template), row: e.row });
  // open the first design once the list arrives, and keep the open one in step after a save
  useEffect(() => {
    if (!draft) { if (entries[0]) open(entries[0]); return; }
    if (draft.key === 'new' || dirty) return;
    const now = entries.find((x) => x.key === draft.key);
    if (now) setDraft({ key: now.key, kind: now.kind, template: structuredClone(now.template), row: now.row });
    else if (entries[0]) open(entries[0]); else setDraft(null);
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const leave = (then: () => void) => {
    if (!dirty) { then(); return; }
    setConfirm({ title: 'Discard your changes?', message: 'This design has changes you have not saved.', run: async () => { setDirty(false); then(); } });
  };
  const pick = (e: TemplateEntry) => leave(() => { open(e); setDirty(false); });
  const startNew = (t: Template, target: 'mine' | 'base', extra?: Partial<Draft>) => leave(() => {
    setDraft({ key: 'new', kind: 'new', template: { ...structuredClone(t), id: '' }, target, ...extra }); setDirty(true);
  });

  const readOnly = draft?.kind === 'shared';
  const copyOnSave = draft?.kind === 'base' && !canBase;
  const isOwn = draft?.kind === 'mine' || draft?.kind === 'team';

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const t = draft.template;
      if (draft.kind === 'new' && draft.target === 'base') {
        const out = await saveTemplates(clientId, [...visible.base, t]);
        onBase(out); setDirty(false);
        const added = out.kit.templates[out.kit.templates.length - 1];
        await refresh(); setDraft({ key: `base:${added.id}`, kind: 'base', template: structuredClone(added) });
        toast.success('Added to the base templates');
      } else if (draft.kind === 'new' || copyOnSave) {
        const r = await createMyTemplate(clientId, {
          name: copyOnSave ? `My copy of ${t.name}` : t.name, template: t,
          based_on: copyOnSave ? t.id : draft.based_on ?? null, copy_of: draft.copy_of ?? null,
        });
        setDirty(false); await refresh();
        setDraft({ key: `ut:${r.id}`, kind: 'mine', template: structuredClone(r.template), row: r });
        toast.success(copyOnSave ? 'Saved as your own copy. The base template stays as it was.' : 'Saved to My templates');
      } else if (draft.kind === 'base') {
        const out = await saveTemplates(clientId, visible.base.map((b) => (b.id === t.id ? t : b)));
        onBase(out); setDirty(false); await refresh();
        toast.success('Base template saved');
      } else if (draft.row) {
        const r = await saveMyTemplate(clientId, draft.row.id, { name: t.name, template: t });
        setDirty(false); await refresh();
        setDraft({ key: `ut:${r.id}`, kind: draft.kind, template: structuredClone(r.template), row: r });
        toast.success('Saved');
      }
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const copyIt = async (e: { template: Template; row?: UserTemplate }) => {
    try {
      const r = await createMyTemplate(clientId, { name: `Copy of ${e.template.name}`, template: e.template,
        based_on: e.row ? e.row.based_on : e.template.id, copy_of: e.row?.id ?? null });
      await refresh(); setDirty(false);
      setDraft({ key: `ut:${r.id}`, kind: 'mine', template: structuredClone(r.template), row: r });
      toast.success('Copy saved to My templates');
    } catch (err) { toast.error(errText(err)); }
  };
  const fromHere = (): Partial<Draft> => ({
    based_on: draft?.kind === 'base' ? draft.template.id : draft?.row?.based_on ?? null,
    copy_of: draft?.row?.id ?? null,
  });
  const dupTarget: 'mine' | 'base' = draft?.kind === 'base' && canBase ? 'base' : 'mine';

  const removeDraft = () => {
    if (!draft) return;
    if (draft.kind === 'new') { setConfirm({ title: 'Discard this new design?', message: 'It has not been saved.', run: async () => { setDirty(false); setDraft(null); if (entries[0]) open(entries[0]); } }); return; }
    const name = draft.template.name;
    setConfirm({
      title: `Delete "${name}"?`,
      message: draft.kind === 'base' ? 'Everyone on this clinic loses this base template. Copies people made of it stay theirs.' : 'It goes from the list for everyone it was shared with.',
      run: async () => {
        try {
          if (draft.kind === 'base') { const out = await saveTemplates(clientId, visible.base.filter((b) => b.id !== draft.template.id)); onBase(out); }
          else if (draft.row) await deleteMyTemplate(clientId, draft.row.id);
          setDirty(false); setDraft(null); await refresh(); toast.success('Deleted');
        } catch (e) { toast.error(errText(e, 'Could not delete')); }
      },
    });
  };
  const bulkDelete = (ids: string[], what: string) => setConfirm({
    title: `Delete ${what}?`, message: `${ids.length} of your templates go from the list. Nothing else changes.`,
    run: async () => {
      try {
        await api.post(`/clients/${clientId}/brand-kit/my-templates/delete`, { ids });
        if (draft?.row && ids.includes(draft.row.id)) { setDirty(false); setDraft(null); }
        setPicked([]); await refresh(); toast.success(`${ids.length} deleted`);
      } catch (e) { toast.error(errText(e, 'Could not delete')); }
    },
  });
  const promote = async () => {
    if (!draft?.row) return;
    try { const out = await promoteMyTemplate(clientId, draft.row.id); onBase(out); await refresh(); toast.success(`"${draft.row.name}" is now a base template for everyone on this clinic`); }
    catch (e) { toast.error(errText(e)); }
  };

  // the list, grouped: Base, My templates, Shared with me, then (owner, manager) each person's
  const groups: { name: string; items: TemplateEntry[] }[] = [];
  for (const e of entries) {
    const g = groups.find((x) => x.name === e.group) ?? (groups.push({ name: e.group, items: [] }), groups[groups.length - 1]);
    g.items.push(e);
  }
  if (!groups.some((g) => g.name === MINE_GROUP)) groups.splice(groups.findIndex((g) => g.name === BASE_GROUP) + 1, 0, { name: MINE_GROUP, items: [] });
  const mine = visible.mine;
  const copiesOf = (baseId: string) => mine.filter((r) => r.based_on === baseId);
  const baseName = (id: string) => visible.base.find((b) => b.id === id)?.name ?? 'a removed base';
  const copyBases = [...new Set(mine.map((r) => r.based_on).filter(Boolean) as string[])];

  const banner = draft && (
    copyOnSave ? (
      <div className="mb-3 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-200 flex items-start gap-2" data-testid="copy-banner">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>You are editing your own copy. The base template stays as it is. Save puts it in My templates as "My copy of {draft.template.name}".</span>
      </div>
    ) : readOnly ? (
      <div className="mb-3 rounded-lg border border-slate-700 bg-white/5 px-3 py-2 text-sm text-muted-strong flex items-center gap-2 flex-wrap" data-testid="shared-banner">
        <Info size={16} className="shrink-0" />
        <span className="flex-1">Shared with you by {draft.row?.owner_name}. Read only.</span>
        <Button size="sm" icon={<Copy size={14} />} onClick={() => copyIt(draft)} data-testid="use-copy">Use a copy</Button>
      </div>
    ) : draft.kind === 'team' ? (
      <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200 flex items-center gap-2" data-testid="team-banner">
        <Info size={16} className="shrink-0" />
        <span>{draft.row?.owner_name}'s template. You can change it as owner or manager; they see your changes.</span>
      </div>
    ) : draft.kind === 'base' && canBase ? (
      <div className="mb-3 rounded-lg border border-slate-700 bg-white/5 px-3 py-2 text-xs text-muted flex items-center gap-2" data-testid="base-banner">
        <Star size={14} className="shrink-0 text-amber-300" /> Base template: everyone on this clinic starts from it. Your changes reach everyone.
      </div>
    ) : null
  );

  return (
    <div>
      <Card className="mb-4" data-testid="template-list">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex gap-2 flex-wrap items-end">
            <Button size="sm" variant="secondary" icon={<Plus size={14} />} data-testid="add-template"
              onClick={() => startNew(newTemplate(FORMATS[0], 'New template'), canBase ? newTarget : 'mine')}>New template</Button>
            <Button size="sm" icon={<Sparkles size={14} />} data-testid="open-recipes" onClick={() => setRecipes(true)}>Start from a ready design</Button>
            {canBase && (
              <div className="w-52"><Select size="sm" value={newTarget} onChange={(v) => setNewTarget(v as 'mine' | 'base')} data-testid="new-target"
                options={[{ value: 'mine', label: 'New ones go to: My templates' }, { value: 'base', label: 'New ones go to: Base templates' }]} /></div>
            )}
          </div>
          {visible.sees_all && <span className="text-xs text-muted">As owner or manager you see everyone's templates on this clinic.</span>}
        </div>
        <div className="grid gap-3">
          {groups.map((g) => (
            <div key={g.name} data-testid="template-group" data-group={g.name}>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <h3 className="text-xs uppercase tracking-wide text-muted">{g.name}</h3>
                {g.name === MINE_GROUP && mine.length > 0 && (
                  <>
                    <button className="text-xs text-red-400 hover:underline disabled:opacity-40 disabled:no-underline" disabled={!picked.length} data-testid="delete-selected"
                      onClick={() => bulkDelete(picked, `${picked.length} selected template${picked.length === 1 ? '' : 's'}`)}>Delete selected{picked.length ? ` (${picked.length})` : ''}</button>
                    {copyBases.map((b) => (
                      <button key={b} className="text-xs text-red-400 hover:underline" data-testid="delete-copies" data-base={b}
                        onClick={() => bulkDelete(copiesOf(b).map((r) => r.id), `all your copies of "${baseName(b)}"`)}>
                        Delete all my copies of "{baseName(b)}" ({copiesOf(b).length})
                      </button>
                    ))}
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {g.items.length === 0 && <p className="text-xs text-muted">{g.name === MINE_GROUP ? 'Nothing yet. Edit a base template and save, or start a new one: it lands here, private to you.' : 'None'}</p>}
                {g.items.map((e) => {
                  const on = draft?.key === e.key;
                  return (
                    <div key={e.key} className={cn('inline-flex items-center rounded-lg border text-sm', on ? 'border-accent-400 bg-accent-500/10' : 'border-slate-800')}>
                      {e.kind === 'mine' && (
                        <input type="checkbox" className="ml-2" aria-label={`Select ${e.template.name}`} checked={picked.includes(e.row!.id)} data-testid="pick-template"
                          onChange={(ev) => setPicked((p) => (ev.target.checked ? [...p, e.row!.id] : p.filter((x) => x !== e.row!.id)))} />
                      )}
                      <button onClick={() => pick(e)} data-testid="template-chip" data-kind={e.kind} data-key={e.key}
                        className={cn('px-3 h-9 inline-flex items-center gap-1.5', on ? 'text-white' : 'text-muted hover:text-white')}>
                        {e.kind === 'base' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid="base-badge">Base</span>}
                        {e.template.name} <span className="text-xs text-muted">{e.template.w}x{e.template.h}</span>
                        {e.row && (e.row.shared_team || e.row.shared_user_ids.length > 0) && <Users size={12} className="text-sky-300" aria-label="shared with people" />}
                        {e.row?.shared_client && <Building2 size={12} className="text-emerald-300" aria-label="shared with the clinic" />}
                      </button>
                    </div>
                  );
                })}
                {draft?.kind === 'new' && g.name === (draft.target === 'base' ? BASE_GROUP : MINE_GROUP) && (
                  <span className="px-3 h-9 inline-flex items-center rounded-lg border border-dashed border-accent-400 text-sm text-white" data-testid="template-chip-new">{draft.template.name} (not saved)</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {recipes && (
        <RecipeGallery clientId={clientId} kit={kit} people={people} canBase={canBase} onClose={() => setRecipes(false)}
          onPick={(rt, asBase) => { startNew(rt, asBase ? 'base' : 'mine'); setRecipes(false); toast.success(`"${rt.name}" opened. Make it yours, then save.`); }} />
      )}

      {draft ? (
        <>
          <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="template-actions">
            {!readOnly && (
              <Button icon={<Save size={14} />} loading={saving} disabled={!dirty && !copyOnSave} onClick={save} data-testid="template-save">
                {copyOnSave ? 'Save as my copy' : draft.kind === 'new' ? (draft.target === 'base' ? 'Save as a base template' : 'Save to My templates') : dirty ? 'Save changes' : 'Saved'}
              </Button>
            )}
            {(draft.kind === 'base' || draft.kind === 'team') && !dirty && (
              <Button variant="secondary" icon={<Copy size={14} />} onClick={() => copyIt(draft)} data-testid="use-copy-any">Use a copy</Button>
            )}
            {isOwn && draft.row && (
              <Button variant="secondary" icon={<Share2 size={14} />} onClick={() => setShare(draft.row!)} data-testid="template-share">Share</Button>
            )}
            {isOwn && canBase && draft.row && (
              <Button variant="secondary" icon={<Star size={14} />} onClick={promote} disabled={dirty} data-testid="template-promote">Make it a base template</Button>
            )}
            {dirty && <span className="text-xs text-amber-300" data-testid="template-dirty">Unsaved changes</span>}
          </div>
          <TemplateEditor key={draft.key === 'new' ? 'new' : draft.key} clientId={clientId} kit={kit} people={people} template={draft.template}
            historyKey={draft.key} canLock={canBase} readOnly={readOnly} banner={banner}
            onEdit={(fn) => { setDraft((d) => { if (!d) return d; const t = structuredClone(d.template); fn(t); return { ...d, template: t }; }); setDirty(true); }}
            onReplace={(t) => { setDraft((d) => (d ? { ...d, template: t } : d)); setDirty(true); }}
            onDuplicate={readOnly ? undefined : (t) => startNew({ ...t, name: `${t.name} copy` }, dupTarget, fromHere())}
            onAdapt={readOnly ? undefined : (t) => startNew(t, dupTarget, fromHere())}
            onDelete={(draft.kind === 'base' && !canBase) || readOnly ? undefined : removeDraft} />
        </>
      ) : (
        <Card><p className="text-sm text-muted">No templates yet. Start a new one or pick a ready design.</p></Card>
      )}

      {share && (
        <ShareDialog row={share} people={visible.people} clientId={clientId} onClose={() => setShare(null)}
          onSaved={(r) => { setShare(null); refresh(); if (draft?.row?.id === r.id) setDraft((d) => (d ? { ...d, row: r } : d)); }} />
      )}
      <ConfirmDialog open={!!confirm} title={confirm?.title ?? ''} message={confirm?.message} confirmLabel="Yes"
        onCancel={() => setConfirm(null)} onConfirm={async () => { const c = confirm; setConfirm(null); await c?.run(); }} />
    </div>
  );
}
